import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import ActionButton from "../../../../../Components/common/ActionButton/ActionButton";
import SandboxIcon from "../../../../../Components/Icon-SVG/SandboxIcon";
import Modal from "../../../../../Components/UI/Modal/Modal";
import SubmitPopup from "../../../../../Components/common/SubmitPopup/SubmitPopup";
import Content from "../../../../../Components/common/SubmitPopup/Content/Content";
import SandboxTaskStatusBadge from "../../../../../Components/common/AgentSandboxTasks/SandboxTaskStatusBadge";
import SandboxTaskReportsButton from "../../../../../Components/common/AgentSandboxTasks/SandboxTaskReportsButton";
import useSandboxSubmitGuard from "../../../../../Components/common/SubmitPopup/useSandboxSubmitGuard";

import { desktopVmOptions } from "../../../../../Components/common/SubmitPopup/constants";
import { prepareSubmitData } from "../../../../../Components/common/SubmitPopup/utils";
import {
  TASK_POLL_INTERVAL,
  getTaskErrorMessage,
  isTaskActive,
  shouldPollTask,
} from "../../../../../Components/common/AgentSandboxTasks/constants";
import { addNotification } from "../../../../../store/actions/notifierActions";
import {
  RequestAgentSandboxSubmit,
  RequestDetectionSandboxTasks,
} from "../../../../../store/actions/integrationsActions";

const WINDOWS_PATH = /^[a-zA-Z]:\\/;

// Processes carry their image path in `process` when osquery resolved it.
const getDetectionFilePath = (item) =>
  item?.file || item?.path || (WINDOWS_PATH.test(item?.process || "") ? item.process : "");

const getFileName = (filePath) => filePath.split(/[\\/]/).pop();

// "Submit to Sandbox" for a file that lives on the agent machine: the agent
// uploads it on its next poll, so the latest request's progress is shown too.
const DetectionSandboxSubmit = ({ item }) => {
  const dispatch = useDispatch();
  const originalID = new URLSearchParams(useLocation().search).get("data");
  const { hasLicence, availableVmOptions, canOpenSubmitPopup } = useSandboxSubmitGuard();

  const [submitPopupActive, setSubmitPopupActive] = useState(false);
  // SubmitPopup works with an array even for a single file.
  const [submitFile, setSubmitFile] = useState([]);

  const tasks = useSelector((state) => state.integrations.detectionSandboxTasks);
  const submitLoading = useSelector((state) => state.integrations.agentSandboxSubmitLoading);

  const filePath = getDetectionFilePath(item);
  const sha256 = (item?.hashes?.sha256 || item?.sha256 || "").toLowerCase();

  // Tasks are newest first; the store may still hold the previous popup's list.
  const latestTask = tasks.find((task) => task.sha256 === sha256 && task.filePath === filePath);
  const active = isTaskActive(latestTask);
  // Keep polling after submission too, until an analysis report is listable.
  const polling = shouldPollTask(latestTask);

  useEffect(() => {
    if (originalID && filePath && sha256) {
      dispatch(RequestDetectionSandboxTasks({ originalID, sha256 }));
    }
  }, [dispatch, originalID, filePath, sha256]);

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(
      () => dispatch(RequestDetectionSandboxTasks({ originalID, sha256 })),
      TASK_POLL_INTERVAL,
    );
    return () => clearInterval(interval);
  }, [dispatch, polling, originalID, sha256]);

  // Tell the user once the request they are watching finishes.
  const watchedTask = useRef(null);
  useEffect(() => {
    const previous = watchedTask.current;
    watchedTask.current = latestTask ? { id: latestTask.id, status: latestTask.status } : null;

    if (!previous || !latestTask || previous.id !== latestTask.id) return;
    if (!isTaskActive(previous) || isTaskActive(latestTask)) return;

    if (latestTask.status === "Completed") {
      dispatch(
        addNotification({
          message: `${getFileName(filePath)} was collected from the machine and submitted to the sandbox.`,
          options: { variant: "success" },
        }),
      );
    } else if (latestTask.status !== "Cancelled") {
      dispatch(
        addNotification({
          message: getTaskErrorMessage(latestTask) || "Sandbox request failed.",
          options: { variant: "error" },
        }),
      );
    }
  }, [dispatch, latestTask, filePath]);

  if (!originalID || !filePath || !sha256) return null;

  const submitToSandboxHandler = () => {
    if (!canOpenSubmitPopup()) return;
    setSubmitFile([{ fileName: getFileName(filePath), fileHash: sha256 }]);
    setSubmitPopupActive(true);
  };

  const submitHandler = (selectedFiles) => {
    const [options] = prepareSubmitData(selectedFiles, desktopVmOptions);
    dispatch(RequestAgentSandboxSubmit({ originalID, filePath, sha256, options }));
  };

  const renderContent = (
    currentFileIndex,
    activeOs,
    selectedFiles,
    checkboxInputChangeHandler,
    enforceRuntimeChangeHandler,
    otherInputsChangeHandler,
  ) => (
    <Content
      currentFileIndex={currentFileIndex}
      activeOs={activeOs}
      selectedFiles={selectedFiles}
      onCheckboxInputChange={checkboxInputChangeHandler}
      onEnforceRuntimeChange={enforceRuntimeChangeHandler}
      onOtherInputsChange={otherInputsChangeHandler}
    />
  );

  return (
    <>
      {latestTask ? <SandboxTaskStatusBadge task={latestTask} /> : null}
      {latestTask ? <SandboxTaskReportsButton task={latestTask} /> : null}
      <ActionButton
        icon={<SandboxIcon width={14} height={14} color="currentColor" />}
        // Still clickable without a licence, so the user is told why it
        // cannot be submitted; the cursor hints at it beforehand.
        style={!hasLicence ? { cursor: "not-allowed" } : undefined}
        disabled={active || submitLoading}
        title={
          active
            ? "A sandbox request for this file is already waiting for the agent."
            : "The agent uploads the file from its machine and it is submitted to the sandbox."
        }
        onClick={submitToSandboxHandler}>
        Submit to Sandbox
      </ActionButton>
      {submitPopupActive ? (
        <Modal
          active={submitPopupActive}
          setActive={setSubmitPopupActive}
          type="submit-popup"
          className="backdrop--above-popup">
          <SubmitPopup
            selectedFiles={submitFile}
            setSelectedFiles={setSubmitFile}
            vmOptions={availableVmOptions}
            onClose={() => setSubmitPopupActive(false)}
            onSubmit={submitHandler}
            renderContent={renderContent}
            showApplyAll={false}
            showFileList={false}
          />
        </Modal>
      ) : null}
    </>
  );
};

export default DetectionSandboxSubmit;
